<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-25">
      <date-range-picker
        v-model="calendarPeriod"
        :startDate="calendarPeriod.startDate"
        :endDate="calendarPeriod.endDate"
        :locale-data="calendarLocale"
        :ranges="ranges"
        opens="right"
      >
        <md-input v-model="calendarPeriod" slot-scope="picker" />
      </date-range-picker>
    </div>
    <div class="md-layout-item md-size-75" style="padding-top: 6px">
      <input type="checkbox" id="checkbox" v-model="includeZeroDollarDeposits">
      <label for="checkbox">Include $0 deposits</label>
    </div>

    <div class="md-layout-item md-size-100">
      <ApolloQuery
        :query="batchedDepositsQuery"
        :variables="{
          pageSize: $options.pageSize,
          cursor: null,
          forDateRange: forDateRange,
          totalCentsRange: totalCentsRange,
          orderBy: $options.orderBy
        }"
        :notifyOnNetworkStatusChange="false"
      >
        <template v-slot="{ result: { data, fullData }, query }">
          <div v-if="fullData && fullData.errors" class="error apollo">
            <ul>
              <li v-for="error in fullData.errors" :key="error.path.join(':')" class="md-layout-item">
                {{ unauthenticated(error) ? reAuthMessage : error.message }}
              </li>
            </ul>
          </div>
          <div v-if="data && data.batchedDeposits && data.batchedDeposits.edges">
            <md-card>
              <md-card-header class="md-card-header-icon md-card-header-green">
                <div class="card-icon">
                  <md-icon>paid</md-icon>
                </div>
                <h4 class="title">Batched Deposits</h4>
              </md-card-header>
              <md-card-content>
                <md-table
                  id="batchedDepositsTable"
                  :value="data.batchedDeposits.edges"
                  class="paginated-table table-striped table-hover"
                  @md-selected="batchedDepositSelected"
                >
                  <md-table-row
                    slot="md-table-row"
                    slot-scope="{ item: { node } }"
                    md-selectable="single"
                  >
                    <md-table-cell md-label="Date">
                      {{ node.for | formatDate }}
                    </md-table-cell>
                    <md-table-cell md-label="Merchant">
                      {{ node.merchant.name }}
                    </md-table-cell>
                    <md-table-cell md-label="Street Address">
                      {{ node.merchant.streetAddress }}
                    </md-table-cell>
                    <md-table-cell md-label="Total">
                      {{ formatBalance(node.totalCents) }}
                    </md-table-cell>
                    <md-table-cell md-label="Submitted">
                      {{ node.submittedAt | formatDatetime }}
                    </md-table-cell>
                    <md-table-cell md-label="Status">
                      <badge v-if="node.lastKnownTransactionStatus === 'sent'" type="success">
                        {{ node.lastKnownTransactionStatus }}
                      </badge>
                      <badge v-else-if="node.lastKnownTransactionStatus === 'failed'" type="danger">
                        {{ node.lastKnownTransactionStatus }}
                      </badge>
                      <badge v-else-if="node.lastKnownTransactionStatus === 'pending'" type="info">
                        {{ node.lastKnownTransactionStatus }}
                      </badge>
                      <template v-else>
                        {{ node.lastKnownTransactionStatus }}
                      </template>
                    </md-table-cell>
                  </md-table-row>
                </md-table>
              </md-card-content>
            </md-card>

            <md-button
              class="md-info"
              v-if="data.batchedDeposits.pageInfo.hasNextPage"
              :disabled="isLoadingMore"
              @click="loadMore(query, data.batchedDeposits.pageInfo.endCursor)"
            >
              Load more
            </md-button>
          </div>
        </template>
      </ApolloQuery>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import DateRangePicker from 'vue2-daterange-picker'
import { BatchedDepositsQuery } from "@/graphql.js";
import { Badge } from "@/components";
import IntToMoneyFormatter from "@/mixins/money-formatter-mixin.js"

export default {
  name: "BatchedDeposits",
  mixins: [ IntToMoneyFormatter ],
  components: {
    Badge,
    DateRangePicker,
  },
  pageSize: 50,
  orderBy: { for: 'desc' },
  data() {
    return {
      isLoadingMore: false,
      forDateRange: {
        lowerBound: moment().startOf('month').format('YYYY-MM-DD'),
        upperBound: moment().endOf('month').format('YYYY-MM-DD'),
        upperInclusive: true,
      },
      includeZeroDollarDeposits: false,
      calendarPeriod: {
        startDate: moment().startOf('month'),
        endDate: moment().endOf('month')
      },
      calendarLocale: {
        direction: 'ltr',
        format: 'MM/DD/YYYY',
        separator: ' - ',
        applyLabel: 'Apply',
        cancelLabel: 'Cancel',
        weekLabel: 'W',
        customRangeLabel: 'Custom Range',
        daysOfWeek: moment.weekdaysMin(),
        monthNames: moment.monthsShort(),
        firstDay: 1,
        showWeekNumbers: true
      },
      ranges: {
        'This week': [
          moment().isoWeekday('Monday'),
          moment().isoWeekday('Sunday')
        ],
        'Last week': [
          moment().subtract(1, 'week').isoWeekday('Monday'),
          moment().subtract(1, 'week').isoWeekday('Sunday')
        ],
        'This month': [
          moment().startOf('month'),
          moment().endOf('month')
        ],
        'Last month': [
          moment().subtract(1, 'month').startOf('month'),
          moment().subtract(1, 'month').endOf('month')
        ],
        'All Time': [
          moment('2018', 'YYYY').startOf('year'),
          moment().endOf('day')
        ],
      },
      reAuthMessage: (
        "You're session expired."
        + " We should prioritize shortcut story 3444"
        + " to get automatic re-authentication working with graphql."
      ),
    }
  },
  computed: {
    totalCentsRange() {
      return {
        lowerBound: (this.includeZeroDollarDeposits ? null : 1),
        upperBound: null,
        upperInclusive: true,
      };
    },
  },
  methods: {
    batchedDepositsQuery() {
      return BatchedDepositsQuery;
    },
    batchedDepositSelected(item) {
      const routeData = this.$router.resolve({
        name: 'Batched Deposit',
        params: { nodeId: item.node.id }
      });
      window.open(routeData.href, '_blank');
    },
    async loadMore(query, endCursor) {
      this.isLoadingMore = true;
      try {
        await query.fetchMore({
          variables: { cursor: endCursor },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.batchedDeposits.edges;
            const pageInfo = fetchMoreResult.batchedDeposits.pageInfo;

            return newEdges.length ? {
              ...previousResult,
              batchedDeposits: {
                ...previousResult.batchedDeposits,
                edges: [...previousResult.batchedDeposits.edges, ...newEdges],
                pageInfo,
              }
            } : previousResult
          },
        })
      } catch (e) {
        if (e.graphQLErrors.some(this.unauthenticated)) {
          alert(this.reAuthMessage);
        } else {
          alert(e);
        }
      } finally {
        this.isLoadingMore = false;
      }
    },
    unauthenticated(error) {
      return error.extensions && error.extensions.code === "UNAUTHENTICATED";
    },
  },
  watch: {
    calendarPeriod: {
      handler() {
        this.forDateRange = {
          lowerBound: moment(this.calendarPeriod.startDate).format('YYYY-MM-DD'),
          upperBound: moment(this.calendarPeriod.endDate).format('YYYY-MM-DD'),
          upperInclusive: true,
        };
      },
      deep: true
    }
  },
  filters: {
    formatDate(str) {
      return moment(str).format('MM/DD/YY');
    },
    formatDatetime(str) {
      return str
        ? moment(str).utc().tz('America/New_York').format('hh:mm MM/DD/YY A z')
        : '';
    },
  },
}
</script>

<style lang="scss" scoped>
#batchedDepositsTable tr td:nth-child(2), td:nth-child(5) {
  max-width: 1% !important;
  white-space: nowrap;
}
</style>
